import { useCallback, useEffect, useState } from 'react';
import { fetchPendingDeletionContracts, restoreContract } from '../../api/contract';
import { Contract } from '../../types/contract';
import './ContractPendingDeletion.css';

import { reportError } from '../../utils/notify';
interface ContractPendingDeletionProps {
  onRestored?: () => void;
  onOpenDetail?: (contractId: number) => void;
}

function ContractPendingDeletion({ onRestored, onOpenDetail }: ContractPendingDeletionProps) {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const list = await fetchPendingDeletionContracts();
      setContracts(list);
      setError('');
    } catch {
      reportError(setError, '삭제 예정 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRestore = async (contract: Contract) => {
    const ok = window.confirm(`'${contract.contractName}' 계약서를 복구할까요?`);
    if (!ok) return;
    try {
      setRestoringId(contract.contractId);
      await restoreContract(contract.contractId);
      await load();
      onRestored?.();
    } catch {
      reportError(setError, '복구에 실패했습니다. 오너/팀장 권한을 확인해 주세요.');
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <div className="contract-pending">
      <div className="contract-pending__toolbar">
        <div>
          <h2>삭제 예정</h2>
          <p>
            등록된 계약서 삭제 후 7일간 보관됩니다. 행 더블클릭으로 상세를 확인하고 복구할 수
            있습니다.
          </p>
        </div>
        <button type="button" className="contract-pending__ghost" onClick={() => void load()}>
          새로고침
        </button>
      </div>

      {error && <p className="contract-pending__error">{error}</p>}

      <div className="contract-pending__content">
        {loading ? (
          <p className="contract-pending__hint">불러오는 중...</p>
        ) : contracts.length === 0 ? (
          <div className="contract-pending__empty">
            <strong>삭제 예정인 계약서가 없습니다.</strong>
            <span>목록에서 등록된 계약서를 삭제하면 이곳에 표시됩니다.</span>
          </div>
        ) : (
          <table className="ec-table contract-pending__table">
            <thead>
              <tr>
                <th style={{ width: '120px' }}>문서번호</th>
                <th style={{ width: '110px' }}>계약번호</th>
                <th>계약명</th>
                <th style={{ width: '120px' }}>발주처</th>
                <th style={{ width: '80px' }}>삭제자</th>
                <th style={{ width: '150px' }}>삭제일시</th>
                <th style={{ width: '150px' }}>자동삭제 예정</th>
                <th style={{ width: '100px' }}>금액</th>
                <th style={{ width: '90px' }}>복구</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((contract) => (
                <tr
                  key={contract.contractId}
                  className={selectedId === contract.contractId ? 'is-selected' : ''}
                  onClick={() => setSelectedId(contract.contractId)}
                  onDoubleClick={() => onOpenDetail?.(contract.contractId)}
                >
                  <td>{contract.documentNo || '-'}</td>
                  <td>{contract.contractNo || '-'}</td>
                  <td>{contract.contractName}</td>
                  <td>{contract.clientName || '-'}</td>
                  <td>{contract.deletedUserName || '-'}</td>
                  <td>{formatDateTime(contract.deletedAt)}</td>
                  <td>
                    <span className="contract-pending__purge">{formatDateTime(contract.purgeAt)}</span>
                  </td>
                  <td className="contract-pending__amount">
                    {formatWon(contract.totalAmount ?? 0)}
                  </td>
                  <td>
                    {contract.canRestore ? (
                      <button
                        type="button"
                        className="contract-pending__restore"
                        disabled={restoringId === contract.contractId}
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleRestore(contract);
                        }}
                      >
                        {restoringId === contract.contractId ? '복구 중...' : '복구'}
                      </button>
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function formatWon(value: number) {
  return `${value.toLocaleString('ko-KR')}원`;
}

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default ContractPendingDeletion;
